
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BookOpen, Clock, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Lesson as LessonType } from "@/types/lesson";

interface LessonProps {
  lesson: LessonType;
  isLocked: boolean; // Keeping for type compatibility but won't use
  isCompleted: boolean;
  isInProgress: boolean;
  previousLessonCompleted: boolean; // Keeping for type compatibility but won't use
  onClick: () => void;
}

/**
 * Enhanced Lesson component that displays an individual lesson card with status and progression
 * Now optimized to work with progress overlay from LessonCard wrapper
 * @param lesson - The lesson data to display
 * @param isCompleted - Whether the user has completed this lesson
 * @param isInProgress - Whether the user is currently working on this lesson
 * @param onClick - Handler for when the lesson card is clicked
 */
export const Lesson = ({ 
  lesson, 
  isCompleted,
  isInProgress,
  onClick 
}: LessonProps) => {
  return (
    <Card 
      onClick={onClick}
      className={cn(
        "group relative transition-all duration-300 border-gray-100/50 min-h-[200px]",
        "hover:shadow-lg hover:scale-[1.02] cursor-pointer pb-20", // Added pb-20 for progress overlay space
        isCompleted ? "bg-gradient-to-br from-green-50 to-emerald-50" :
        isInProgress ? "bg-gradient-to-br from-blue-50 to-indigo-50" :
        "bg-gradient-to-br from-white to-gray-50"
      )}
    >
      {/* Progress Line Connector */}
      <div className={cn(
        "absolute -left-4 top-1/2 w-8 h-1 -translate-y-1/2",
        isCompleted ? "bg-green-500" :
        isInProgress ? "bg-blue-500" :
        "bg-gray-300",
        "first:hidden" // Hide for first lesson
      )} />
      
      <CardHeader>
        <div className="flex items-center gap-4">
          <div className={cn(
            "p-3 rounded-full transition-transform duration-300 shadow-sm",
            isCompleted ? "bg-green-100" : 
            isInProgress ? "bg-blue-100" :
            "bg-background",
            "group-hover:scale-110",
            "relative z-10" // Ensure icon is above connector line
          )}>
            {isCompleted ? (
              <CheckCircle className="h-6 w-6 text-green-600" />
            ) : (
              <BookOpen className="h-6 w-6 text-primary" />
            )}
          </div>
          <div className="flex-1">
            <CardTitle className="text-xl leading-tight">
              {lesson.title}
            </CardTitle>
            <CardDescription className="text-gray-600 mt-1">
              {lesson.description}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-sm flex items-center gap-2 text-gray-600">
          <Clock className="h-4 w-4" />
          Duration: {lesson.duration} minutes
          {isInProgress && (
            <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full text-xs animate-pulse">
              In Progress
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
